import React from "react";
import { motion } from "framer-motion";
import {
  FaMagic,
  FaPaperPlane,
  FaHeadset,
  FaMapMarkedAlt,
  FaFileInvoice,
  FaTools,
} from "react-icons/fa"; // 適当なアイコンを使用

import { Link } from "react-router-dom";

const otherSteps = [
  {
    title: "要件整理",
    description:
      "設置目的・場所・録画方式などを明確にし、必要な機能を整理します。様々な機能をそろえたカメラがあります。カメラの選定は専門的な知識が必要なため、適切な機種を見極めるのに時間や手間がかかることがあります。",
  },
  {
    title: "業者選定",
    description:
      "カメラの管理会社と設置業者は一般的に別の会社が運営しています。そのため仲介業者などが入り設置業者を決定します。また、業界の構造上、複数の業者を経由する体制が一般的で、現場の対応までに時間を要するケースも少なくありません。",
  },
  {
    title: "業者決定",
    description:
      "設置場所や規模に応じて、適切な工事業者を選定します。屋内配線や屋外設置、配管工事などの条件に合う施工業者を探す必要があります。",
  },
  {
    title: "現地調査",
    description:
      "実際の設置場所（建物構造・配線ルート・死角の有無など）を確認する工程で、設計の精度や必要機材の判断に直結する重要なステップです。",
  },
  {
    title: "見積依頼",
    description: "カメラ設置・配線・操作説明を含む施工を実施します。",
  },
  {
    title: "設置工事",
    description:
      "カメラ本体の取り付け、配線、録画機器との接続など、現場での物理的な施工を実施します。",
  },
];

// img → icon に変換済み
const ourSteps = [
  {
    title: "直感で、最適を選ぶ",
    icon: FaMagic,
    description:
      "カメラ選びはシンプルに。数問の質問に直感でタップしていくだけで、最適なカメラをご提案。選択に迷わず、すぐに結果が手に入ります。",
  },
  {
    title: "ワンタップで情報送信",
    icon: FaPaperPlane,
    description:
      "結果はそのままコピー＆ペースト。余計な入力や説明は一切不要です。気に入った内容を、お問い合わせフォームに貼り付けて送信してください。",
  },
  {
    title: "導入サポート",
    icon: FaHeadset,
    description:
      "ご連絡を受け次第、こちらで最適な業者を手配。現地調査の段取りもすぐに進めます。お客様の手間はゼロ。業務に集中していただけます。",
  },
  {
    title: "現地調査で最終確認",
    icon: FaMapMarkedAlt,
    description:
      "スタッフが設置予定の場所へ伺い、現地の条件を確認します。配線や視野、取り付けの可否などを事前にチェック。導入前のズレを未然に防ぎます。",
  },
  {
    title: "見積もりをご案内",
    icon: FaFileInvoice,
    description:
      "調査結果に基づき、内容とコストを最適化したお見積もりをご提示。納得感のある提案を心がけていますので、ご不明点はいつでもご相談ください。",
  },
  {
    title: "設置工事",
    icon: FaTools,
    description:
      "経験豊富なスタッフが、丁寧かつスムーズに施工を実施。完了後はすぐに使える状態でお引き渡し。不具合やご相談があれば、いつでもお気軽にご連絡ください。",
  },
];

const StepFlow = ({ steps }) => {
  const isOther = steps[0]?.icon === undefined;

  if (isOther) {
    return (
      <div className="space-y-6 text-sm leading-relaxed sm:w-1/2 m-auto hidden">
        {steps.map((step, i) => (
          <div key={i}>
            <strong className="block">
              STEP {i + 1}. {step.title}
            </strong>
            {step.description}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className={`grid grid-cols-1 ${steps.length > 4 ? "md:grid-cols-3 md:grid-rows-2" : "md:grid-cols-4"} gap-6 w-full`}
    >
      {steps.map((step, i) => {
        const Icon = step.icon;
        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.4, delay: i * 0.2 }}
            className="bg-white text-black px-4 py-6 rounded-xl shadow h-[300px] flex flex-col justify-start text-center overflow-hidden"
          >
            <div className="text-xl font-semibold text-blue-900 mb-1">STEP {i + 1}</div>
            <div className="flex flex-col justify-between h-full w-full">
              <div className="text-blue-950 text-7xl mt-4 mx-auto">{Icon && <Icon />}</div>
              <div className="w-full">
                <h3 className="font-bold text-xl mb-2">{step.title}</h3>
                <p className="text-xs leading-relaxed px-1 text-left">{step.description}</p>
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

const ContactSection = () => {
  return (
    <div className="bg-[#002b5b] text-white py-12 px-6 text-center rounded-xl w-full max-w-4xl mx-auto mt-16 hidden">
      <h2 className="font-bold mb-4 text-[clamp(12px,8vw,20px)]">まずはお気軽にご相談ください</h2>
      <p className="mb-6 text-sm md:text-base">
        専門スタッフが迅速にご対応します。カメラの選定、比較、ご提案など
        <br className="hidden md:block" />
        ワンストップでお手伝いします。
      </p>
      <button className="block bg-white mx-auto text-[#002b5b] font-semibold py-3 px-6 rounded-full shadow hover:bg-gray-100 transition">
        <Link to="/contact">お問い合わせはこちら</Link>
      </button>
    </div>
  );
};

export default function FlowComparison() {
  return (
    <div className="space-y-16 px-4 w-full">
      <div className="w-full">
        <h3 className="font-bold text-center mb-4 text-lg hidden">他社フロー</h3>
        <StepFlow steps={otherSteps} />
      </div>
      <div className="w-full ">
        <>
          <h3 className="font-bold text-left text-6xl ">More Simply</h3>
          <p className="mt-1 mb-24 pl-1 underline text-xl ">私たちが実現するシンプルな発注</p>
        </>
        <StepFlow steps={ourSteps} />
      </div>
      <ContactSection />
    </div>
  );
}
